import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, AlertTriangle, Package, Building2, Calendar, DollarSign } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

interface POEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  poData: any;
  remarks: string;
  onSave: (updatedData: any) => Promise<boolean>;
  vendors: any[];
  skus: any[];
}

interface POItem {
  id: string;
  skuId: number;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

const POEditModal: React.FC<POEditModalProps> = ({
  isOpen,
  onClose,
  poData,
  remarks,
  onSave,
  vendors,
  skus
}) => {
  const [formData, setFormData] = useState({
    poNumber: '',
    vendorId: 0,
    priority: 'Medium',
    notes: '',
    deliveryDetails: {
      address: '',
      instructions: '',
      expectedDate: ''
    },
    items: [] as POItem[]
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (poData && isOpen) {
      setFormData({
        poNumber: poData.poNumber || '',
        vendorId: poData.vendorId || 0,
        priority: poData.priority || 'Medium',
        notes: poData.notes || '',
        deliveryDetails: {
          address: poData.deliveryDetails?.address || '',
          instructions: poData.deliveryDetails?.instructions || '',
          expectedDate: poData.deliveryDetails?.expectedDate || ''
        },
        items: poData.items?.map((item: any, index: number) => ({
          id: item.id || `item-${index}`,
          skuId: item.skuId || 0,
          quantity: item.quantity || 0,
          unitPrice: item.unitPrice || 0,
          totalPrice: (item.quantity || 0) * (item.unitPrice || 0)
        })) || []
      });
      setErrors({});
    }
  }, [poData, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.poNumber.trim()) {
      newErrors.poNumber = 'PO Number is required';
    }

    if (!formData.vendorId) {
      newErrors.vendorId = 'Vendor is required';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      if (!item.skuId) {
        newErrors[`item-${index}-sku`] = 'SKU is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`item-${index}-quantity`] = 'Valid quantity is required';
      }
      if (!item.unitPrice || item.unitPrice <= 0) {
        newErrors[`item-${index}-price`] = 'Valid unit price is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleDeliveryChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      deliveryDetails: {
        ...prev.deliveryDetails,
        [field]: value
      }
    }));
  };

  const handleItemChange = (index: number, field: keyof POItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.totalPrice = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    }));

    // Clear item-specific errors
    const errorKey = `item-${index}-${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: `item-${Date.now()}`,
          skuId: 0,
          quantity: 0,
          unitPrice: 0,
          totalPrice: 0
        }
      ]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const getSKUName = (skuId: number) => {
    const sku = skus.find(s => s.id === skuId);
    return sku ? sku.name : 'Select SKU';
  };

  const getVendorName = (vendorId: number) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? vendor.name : 'Select Vendor';
  };

  const calculateTotalAmount = () => {
    return formData.items.reduce((total, item) => total + item.totalPrice, 0);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setLoading(true);
    try {
      const updatedData = {
        ...poData,
        ...formData,
        totalAmount: calculateTotalAmount(),
        updatedAt: new Date().toISOString()
      };

      const success = await onSave(updatedData);
      if (success) {
        toast.success('PO updated successfully');
        onClose();
      } else {
        toast.error('Failed to update PO');
      }
    } catch (error) {
      console.error('Error saving PO:', error);
      toast.error('Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const selectedVendor = vendors.find(v => v.id === formData.vendorId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-0">
        {/* Fixed Header */}
        <div className="sticky top-0 z-10 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              <div>
                <h2 className="text-lg font-semibold">Edit Purchase Order - {formData.poNumber}</h2>
                <p className="text-sm text-muted-foreground">
                  Make the necessary changes as requested in the revision remarks below.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Revision Alert */}
          <Alert className="border-yellow-200 bg-yellow-50 mb-6">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Revision Required:</strong> {remarks}
            </AlertDescription>
          </Alert>

          {/* Items - Moved right after alert */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Items
                </CardTitle>
                <Button onClick={addItem} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
              {errors.items && (
                <p className="text-sm text-red-500">{errors.items}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={item.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium">Item {index + 1}</h4>
                      {formData.items.length > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>SKU *</Label>
                        <Select
                          value={item.skuId.toString()}
                          onValueChange={(value) => handleItemChange(index, 'skuId', parseInt(value))}
                        >
                          <SelectTrigger className={errors[`item-${index}-sku`] ? 'border-red-500' : ''}>
                            <SelectValue placeholder="Select SKU" />
                          </SelectTrigger>
                          <SelectContent>
                            {skus.map((sku) => (
                              <SelectItem key={sku.id} value={sku.id.toString()}>
                                {sku.name} ({sku.skuCode})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors[`item-${index}-sku`] && (
                          <p className="text-sm text-red-500">{errors[`item-${index}-sku`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Quantity *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                          className={errors[`item-${index}-quantity`] ? 'border-red-500' : ''}
                        />
                        {errors[`item-${index}-quantity`] && (
                          <p className="text-sm text-red-500">{errors[`item-${index}-quantity`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Unit Price *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          className={errors[`item-${index}-price`] ? 'border-red-500' : ''}
                        />
                        {errors[`item-${index}-price`] && (
                          <p className="text-sm text-red-500">{errors[`item-${index}-price`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Total Price</Label>
                        <div className="flex items-center h-10 px-3 border rounded-md bg-muted">
                          <span className="text-sm font-medium">{formatCurrency(item.totalPrice)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Separator className="my-4" />

              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-2xl font-bold">{formData.items.length}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="text-2xl font-bold text-primary">{formatCurrency(calculateTotalAmount())}</p>
                </div>
              </div>
            </CardContent>
          </Card>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="poNumber">PO Number *</Label>
                  <Input
                    id="poNumber"
                    value={formData.poNumber}
                    onChange={(e) => handleInputChange('poNumber', e.target.value)}
                    className={errors.poNumber ? 'border-red-500' : ''}
                  />
                  {errors.poNumber && (
                    <p className="text-sm text-red-500">{errors.poNumber}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor *</Label>
                  <Select
                    value={formData.vendorId.toString()}
                    onValueChange={(value) => handleInputChange('vendorId', parseInt(value))}
                  >
                    <SelectTrigger className={errors.vendorId ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select Vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id.toString()}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.vendorId && (
                    <p className="text-sm text-red-500">{errors.vendorId}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => handleInputChange('priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expectedDate">Expected Delivery Date</Label>
                  <Input
                    id="expectedDate"
                    type="date"
                    value={formData.deliveryDetails.expectedDate}
                    onChange={(e) => handleDeliveryChange('expectedDate', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Additional notes or comments..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Delivery Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Delivery Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Delivery Address</Label>
                <Textarea
                  id="address"
                  value={formData.deliveryDetails.address}
                  onChange={(e) => handleDeliveryChange('address', e.target.value)}
                  rows={3}
                  placeholder="Enter delivery address..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instructions">Delivery Instructions</Label>
                <Textarea
                  id="instructions"
                  value={formData.deliveryDetails.instructions}
                  onChange={(e) => handleDeliveryChange('instructions', e.target.value)}
                  rows={2}
                  placeholder="Special delivery instructions..."
                />
              </div>
            </CardContent>
          </Card>

        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default POEditModal;
